import React, { Component, ErrorInfo, ReactNode } from "react";
import { clearAuthTokens } from "../../services/apiService";
import { isChunkLoadError, reloadOnceForChunkError } from "./lazyWithRetry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary - catches any uncaught render error so the app
 * never goes to a blank screen.  Gives the user a clear "something broke"
 * message plus a one-click recovery button that clears potentially-corrupt
 * local storage data and reloads.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A failed code-split import (e.g. an old tab loading a chunk that a new
    // deploy removed) is NOT an auth problem — never wipe tokens for it. Just
    // reload once to pull the fresh build. The guard prevents a reload loop.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return;
    }
    console.error("[ErrorBoundary] Uncaught render error:", error, errorInfo);
  }

  handleRecover = () => {
    // Wipe auth tokens (most common source of corruption)
    try {
      clearAuthTokens();
      localStorage.removeItem("darkMode");
      localStorage.removeItem("selectedTab");
    } catch {
      // localStorage itself might be broken - best-effort
    }
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Chunk-load failures mean a new version shipped while this tab was open.
      // Show a calm "updating" splash with a plain reload (no token wipe). In
      // the common case componentDidCatch has already triggered the reload, so
      // this only flashes briefly.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div
            style={{
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "#01183a",
              color: "#fff",
              fontFamily: "system-ui, sans-serif",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <h1 style={{ fontSize: "1.75rem", marginBottom: "0.5rem" }}>
              Updating to the latest version…
            </h1>
            <p style={{ opacity: 0.7, maxWidth: 480, marginBottom: "1.5rem" }}>
              A new version of BookieBrawler was just released. Reloading to get
              the latest. If this doesn't clear, tap reload.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "0.75rem 2rem",
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: "8px",
                border: "none",
                background: "#97c5df",
                color: "#01183a",
                cursor: "pointer",
              }}
            >
              Reload
            </button>
          </div>
        );
      }

      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "#01183a",
            color: "#fff",
            fontFamily: "system-ui, sans-serif",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ opacity: 0.7, maxWidth: 480, marginBottom: "1.5rem" }}>
            An unexpected error occurred. This is usually caused by a stale
            session - click below to clear it and reload.
          </p>
          <button
            onClick={this.handleRecover}
            style={{
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: "8px",
              border: "none",
              background: "#4a9eda",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Clear Session &amp; Reload
          </button>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre
              style={{
                marginTop: "2rem",
                padding: "1rem",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "8px",
                maxWidth: 600,
                overflow: "auto",
                fontSize: "0.8rem",
                textAlign: "left",
              }}
            >
              {this.state.error.message}
              {"\n"}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
